/**
 * MercadoPago v1 SDK script injection.
 *
 * Lives in shared/ (not the booking feature) so TenantProvider can preload
 * the SDK as soon as tenant resolution reports paymentProcessor
 * 'mercadopago', without a core → features import.
 *
 * A script tag that failed to load stays in the DOM looking identical to
 * one that is still downloading, which is how a single network blip used
 * to lock the whole SPA session out of the payment form (the old hook
 * skipped injection whenever the tag existed). The `data-mp-failed` marker
 * set by the error listener is what lets consumers tell the two apart and
 * replace dead tags.
 */

const MP_SDK_URL = 'https://secure.mlstatic.com/sdk/javascript/v1/mercadopago.js';

export const MP_SCRIPT_ID = 'mercadopago-sdk-script';
export const MP_FAILED_ATTR = 'mpFailed';

function isMpSdkLoaded(): boolean {
  return typeof window.Mercadopago !== 'undefined';
}

/**
 * Injects the SDK script tag, replacing a previously failed one if present.
 * Returns the live element so callers can attach their own error listeners.
 *
 * `attempt` > 1 appends a cache-busting query param: if an intermediary
 * (carrier proxy, corporate cache) poisoned the cached response, retrying
 * the exact same URL would replay the same failure.
 */
export function ensureMercadoPagoScript(attempt = 1): HTMLScriptElement {
  let script = document.getElementById(MP_SCRIPT_ID) as HTMLScriptElement | null;

  if (script && script.dataset[MP_FAILED_ATTR] === '1') {
    script.remove();
    script = null;
  }

  if (!script) {
    const el = document.createElement('script');
    el.src = attempt > 1 ? `${MP_SDK_URL}?attempt=${attempt}` : MP_SDK_URL;
    el.id = MP_SCRIPT_ID;
    el.async = true;
    el.addEventListener(
      'error',
      () => {
        el.dataset[MP_FAILED_ATTR] = '1';
      },
      { once: true },
    );
    document.body.appendChild(el);
    script = el;
  }

  return script;
}

/**
 * Removes the current script tag (dead or in-flight) so the next
 * ensureMercadoPagoScript call injects a fresh one.
 */
export function removeMercadoPagoScript(): void {
  document.getElementById(MP_SCRIPT_ID)?.remove();
}

/**
 * Fire-and-forget preload for MP tenants, called from TenantProvider.
 * Gives the 25KB SDK the whole booking flow to download instead of the
 * 15 seconds the payment step used to allow on slow mobile connections.
 * useMercadoPagoSDK owns retries and key configuration; this only warms
 * the fetch.
 */
export function preloadMercadoPagoSDK(): void {
  if (isMpSdkLoaded()) return;
  ensureMercadoPagoScript();
}
